/**
 * All functionality needed in Media related components.
 *
 * @module feature/Media
 */
import Repository from 'Repository';
import Filter from '../../../../../../Foundation/Tags/code/src/scripts/tags/TagFilter.js';
import Pagination from '../../../../../../Foundation/Pagination/code/src/scripts/pagination/Pagination.js';
import ResponsiveBackground from 'ResponsiveBackground';
import EqualHeights from 'EqualHeights';
import Modal from 'Modal';

/**
 * MediaLibrary tagfilter and pagination functionality.
 *
 * @requires module:project/Common.Repository
 * @requires module:project/Common.ResponsiveBackground
 * @requires module:project/Common.EqualHeights
 * @requires module:project/Common.Modal
 * @requires module:foundation/Tags.TagFilter
 * @requires module:foundation/Pagination.Pagination
 * @memberof module:feature/Media
 * @version 1.0.0
 * @author Rocco Janse <rocco.janse@valtech.nl>
 */
class MediaLibrary {
    /**
     * Upgrades DOM element, created filter, pagination and loads repository.
     * @param {DOMElement} element DOM element to upgrade.
     */
    constructor(element) {
        this.$element = $(element);
        this.itemGuid = this.$element.attr('data-guid');
        this.lang = this.$element.attr('data-lang');
        this.maxResults = this.$element.attr('data-items-per-page');

        this.repository = new Repository({
            apiBase: 'api/feature/media/getmediacards',
            itemGuid: this.itemGuid,
            lang: this.lang,
        });
        this.filter = new Filter(this.$element.find('.js-tag-filter'), this.repository).init();
        this.pagination = new Pagination(this.$element.find('.js-pagination'), this.repository, this.maxResults).init();

        this.$loader = this.$element.find('.loader').hide();

        this.$itemList = this.$element.find('.itemlist');
        this.itemTemplate = null;

        this.repository.init();
    }

    /**
     * Builds card template, initializes equal heights and adds listeners to repository.
     */
    init() {
        // get card template
        this.itemTemplate = this.buildTemplate(
            this.$itemList.find('.card--media:not(.card--media-highlight)').first().parent().clone()
        );

        EqualHeights.init();

        // repository listeners
        this.repository.addListener('beforeInit', () => {
            this.$loader.fadeIn(200);
        });
        this.repository.addListener('beforeUpdate', () => {
            this.$loader.fadeIn(200);
        });
        this.repository.addListener('updated', (pagedata) => {
            this.updateList(pagedata);
        });
    }

    /**
     * Updates list with new items.
     * @param {object} data Json object or resultset to render in list.
     */
    updateList(data) {
        this.$loader.fadeIn(200, () => {
            if (data.Success === false) {
                this.$itemList.html(`<div class="alert alert-danger" role="alert">${data.Reason}</div>`);

                // hide loader
                this.$loader.fadeOut(200);
            } else {
                let htmlArray = [];
                let promises = [];

                // render all items
                for (let item of data.Items) {
                    let promise = new Promise((resolve) => {
                        this.renderItem(item).then((renderedItem) => {
                            htmlArray.push(renderedItem);
                            resolve();
                        });
                    });
                    promises.push(promise);
                }

                // update list when all items rendered
                Promise.all(promises).then(() => {
                    // update DOM
                    this.$itemList.html(htmlArray.join(''));

                    EqualHeights.init();

                    // responsive backgrounds
                    this.$itemList.find('.js-responsive-background').each((index, el) => {
                        new ResponsiveBackground($(el)).init();
                    });

                    // modal
                    this.$itemList.find('.js-modal').each((index, el) => {
                        new Modal($(el)).init();
                    });

                    // update valtagmanager
                    if (window.digitalData) {
                        window.digitalData.update();
                    }

                    // hide loader
                    this.$loader.fadeOut(200);
                });
            }
        });
    }

    /**
     * Finds all variable elements and replaces it with tags
     * so we don't need find queries every time we render a card.
     * @param {jQueryDomElement}  $el Card to create template of.
     * @returns {string} HTML string of card.
     */
    buildTemplate($el) {
        const $wrapper = $('<div></div>').append($el);
        $wrapper.find('div').first().attr('class', '%%CLASSNAME%%').prop('style', false);
        $wrapper.find('.card--media').attr('class', '%%CARDCLASS%%');
        $wrapper.find('a').attr('href', '%%ITEMURL%%');
        $wrapper.find('.card__image').prop('style', false);
        $wrapper.find('.img-fluid').attr('src', '');
        $wrapper.find('.card-title').html('%%TITLE%%');
        $wrapper.find('.modal-description').html('%%DESCRIPTION%%');
        $wrapper.find('a.btn-link').attr('href', '%%ATTACHMENTURL%%');

        $wrapper.find('.card__overlay').removeAttr('href');
        $wrapper.find('.card__overlay').attr('data-modal-title', '%%MODALTITLE%%');
        $wrapper.find('.card__overlay').attr('data-modal-description', '%%MODALDESCRIPTION%%');
        $wrapper.find('.card__overlay').attr('data-modal-image', '%%MODALIMAGEURL%%');
        $wrapper.find('.card__overlay').attr('data-modal-dlhref', '%%ATTACHMENTURL%%');
        return $wrapper.html();
    }

    /**
     * Renders item to template.
     * @param {object} item Json object of item to render.
     */
    renderItem(item) {
        return new Promise((resolve) => {
            let html = this.itemTemplate;

            if (item.IsHighlighted === true) {
                html = html.replace(/%%CLASSNAME%%/g, 'col-md-12 col-lg-8 itemlist__item');
                html = html.replace(/%%CARDCLASS%%/g, 'card card--media card--media-highlight');
            } else {
                html = html.replace(/%%CLASSNAME%%/g, 'col-md-6 col-lg-4 itemlist__item');
                html = html.replace(/%%CARDCLASS%%/g, 'card card--media');
            }

            html = html.replace(/%%ITEMURL%%/g, item.Url);
            html = html.replace(/%%TITLE%%/g, item.Title);
            html = html.replace(/%%DESCRIPTION%%/g, item.Description);
            html = html.replace(/%%MODALIMAGEURL%%/g, item.ModalImageUrl);
            html = html.replace(/%%MODALDESCRIPTION%%/g, escape(item.Description));
            html = html.replace(/%%MODALTITLE%%/g, escape(item.Title));
            html = html.replace(/src=""/g, `src="${item.ImageUrl}"` || '');

            if (item.AttachmentUrl === '') {
                item.AttachmentUrl = item.OriginalImageUrl;
            }

            html = html.replace(/%%ATTACHMENTURL%%/g, item.AttachmentUrl);

            resolve(html);
        });
    }
}

// register to Component Handler
window.ComponentHandler.register({
    constructor: MediaLibrary,
    classAsString: 'MediaLibrary',
    cssClass: 'js-media-library',
});

export default MediaLibrary;
